import {
  buildHref,
  type GameDestinationContext,
  type MatchedRoute,
} from "@softmax/web-addresses";

type GamePresentation = {
  home: "watch" | "store" | "desk" | "sugarscape";
  preview: "store" | "gota";
};
const defaultPresentation: GamePresentation = {
  home: "watch",
  preview: "store",
};
// Corpus identity selects presentation independently of a game's public address.
const gamePresentations: Record<string, GamePresentation> = {
  "gods-of-the-arena": { home: "store", preview: "gota" },
  "generals-competition": { home: "store", preview: "store" },
  webdiplomacy: { home: "store", preview: "store" },
  marketing: { home: "desk", preview: "store" },
  sugarscape: { home: "sugarscape", preview: "store" },
};

export function gamePresentationOf(corpus: string): GamePresentation & {
  key: string;
} {
  const slash = corpus.indexOf("/");
  const root = slash === -1 ? corpus : corpus.slice(0, slash);
  const key = root === "generals" ? "generals-competition" : root;
  return { key, ...(gamePresentations[key] ?? defaultPresentation) };
}

export function frontDoorPath({
  corpusSlug,
  route,
  search,
  hash,
}: {
  corpusSlug: string;
  route: GameDestinationContext;
  search?: URLSearchParams;
  hash?: string;
}): string {
  return buildHref({
    name:
      !route.league && gamePresentationOf(corpusSlug).home === "store"
        ? "game.home"
        : "game.preview",
    ...route,
    search,
    hash,
  });
}

function isHomePath({
  route,
  corpusSlug,
}: {
  route: MatchedRoute;
  corpusSlug: string;
}): boolean {
  return (
    route.name === "game.home" &&
    gamePresentationOf(corpusSlug).home === "store"
  );
}

export function isCommunityRoom({
  route,
  corpusSlug,
}: {
  route: MatchedRoute;
  corpusSlug: string;
}): boolean {
  return (
    route.area === "game" &&
    (route.section === "wiki" || route.section === "forum") &&
    gamePresentationOf(corpusSlug).home === "store"
  );
}

export function isCreatorRoom({
  route,
  corpusSlug,
}: {
  route: MatchedRoute;
  corpusSlug: string;
}): boolean {
  const presentation = gamePresentationOf(corpusSlug);
  return (
    isHomePath({ route, corpusSlug }) ||
    isCommunityRoom({ route, corpusSlug }) ||
    route.name === "game.play" ||
    (route.name === "game.preview" && presentation.preview === "gota")
  );
}
